import type { CityConfig } from "../city-template/types";

export const springCreekConfig: CityConfig = {
  city: "Spring Creek",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "spring-creek",
  route: "/cities/spring-creek",
  title: "Emergency Passport Spring Creek NV | Expedited Passport Renewal Elko County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport renewal in Spring Creek, NV — 24-48 hour processing available. Fast passport services for Spring Creek, Elko County, Ruby Valley, and the I-80 corridor. A+ BBB rated, State Dept. registered courier. No agency visit required.",
  h1: "Emergency & Expedited Passport Services in Spring Creek, NV",
  heroSubtitle: "Passport expiring before your trip from Elko County? Fast Passport Center helps Spring Creek, Elko, Carlin, and northeastern Nevada travelers get expedited passports in as little as 24 hours. No agency visit required — Nevada has no local passport agency.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Spring%20Creek%20Nevada%20scenic%20view%20of%20the%20Ruby%20Mountains%20and%20Lamoille%20Canyon%20with%20the%20Spring%20Creek%20planned%20community%20nestled%20in%20the%20high%20desert%20foothills%2C%20wide%20open%20Elko%20County%20landscape%20with%20sagebrush%20and%20mountain%20backdrop%2C%20the%20dramatic%20Ruby%20Mountains%20range%20with%20snow%20capped%20peaks%20towering%20over%20the%20Great%20Basin%20high%20desert%2C%20northeastern%20Nevada%20residential%20community%20with%20mountain%20views%20and%20big%20sky%20atmosphere%2C%20golden%20hour%20light%20on%20the%20Ruby%20Mountain%20front%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=spring-creek-nv-hero-001&orientation=landscape",
  heroImageAlt: "Spring Creek Nevada scenic view of the Ruby Mountains and Lamoille Canyon with high desert landscape",
  geoPosition: "40.7266;-115.5859",
  geoPlacename: "Spring Creek, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St, Suite 600, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 480 miles from Spring Creek via I-80",
  },
  nearbyCitiesZips: [
    { name: "Spring Creek", zips: "89815" },
    { name: "Elko", zips: "89801" },
    { name: "Carlin", zips: "89822" },
    { name: "Wells", zips: "89835" },
    { name: "Battle Mountain", zips: "89820" },
    { name: "Winnemucca", zips: "89445" },
    { name: "West Wendover", zips: "89883" },
    { name: "Lamoille", zips: "89828" },
    { name: "Reno", zips: "89501" },
    { name: "Salt Lake City, UT", zips: "84101" },
    { name: "Twin Falls, ID", zips: "83301" },
    { name: "Jackpot", zips: "89825" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Spring Creek and Elko County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Elko or Spring Creek" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Elko", route: "/cities/elko" },
    { name: "Fast passport renewal in Reno", route: "/cities/reno" },
    { name: "Expedited passport help in Salt Lake City", route: "/cities/salt-lake-city" },
    { name: "Urgent passport services in Boise", route: "/cities/boise" },
    { name: "Same-day passport help in San Francisco", route: "/san-francisco" },
    { name: "Passport courier services in Nevada", route: "/states/nevada" },
  ],
  fpcOfficeAddress: "100 W Liberty St, Reno, NV 89501",
  fpcOfficeNote: "Fast Passport Center serves Spring Creek remotely via secure overnight courier. We do not have a walk-in office in Spring Creek — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Spring Creek, Elko County, and all of northeastern Nevada.",
  fpcOfficeDistance: "Serving all of Spring Creek, Elko, Lamoille, and the Ruby Mountain region",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Spring Creek and Elko County. As Nevada's largest unincorporated community and a popular residential area for Elko mining and professional families, Spring Creek sits at the base of the stunning Ruby Mountains — known as the Swiss Alps of Nevada. We provide fast passport processing for this growing and scenic community.",
  nearbyCitiesIntro: "We serve all Spring Creek neighborhoods and Elko County communities, including Elko, Carlin, Lamoille, Wells, and West Wendover. Our overnight shipping labels work from any UPS location — your northeastern Nevada location doesn't slow us down.",
  howItWorksIntro: "Most Spring Creek travelers complete everything online and ship documents overnight. No need to drive 4+ hours to Reno or 6+ hours to Salt Lake City for a passport agency. Our emergency passport process delivers fast results for Ruby Mountain region travelers.",
  mistakesIntro: "Spring Creek and Elko County residents live in one of the most remote areas of the lower 48 — hundreds of miles from the nearest passport agency. Here are the most common passport mistakes we see in northeastern Nevada.",
  testimonialsIntro: "Spring Creek, Elko, and Ruby Mountain region travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Spring Creek? Here are the most common ones we hear from Elko County and northeastern Nevada travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is San Francisco (480 miles via I-80)",
        "Not even a remote possibility from Spring Creek",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Spring Creek and Elko County travelers with immediate departure needs — as fast as 24 hours for urgent flights from Elko Regional or Salt Lake City International.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance for Spring Creek and Elko County residents.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance — especially helpful for mining industry professionals who travel internationally for work.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Spring Creek Nevada travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Spring Creek NV and Elko County." },
    { name: "New Passport", description: "First-time passport application assistance for Spring Creek and Elko County residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Craig W.",
      location: "Spring Creek, NV",
      rating: 5,
      text: "Mining geologist with Nevada Gold Mines — had a conference in Perth, Australia and my passport was expired. Fast Passport Center renewed it in 48 hours from Spring Creek. I made my flight to Sydney and the conference. Unbelievable service for northeastern Nevada.",
      service: "Emergency Passport",
    },
    {
      name: "Jenny R.",
      location: "Lamoille, NV",
      rating: 5,
      text: "Living in Lamoille Canyon near Spring Creek, I thought passport processing would take forever out here. Fast Passport Center proved me wrong — 3 days for a renewal. The Ruby Mountains may be remote, but this service makes the world accessible.",
      service: "Passport Renewal",
    },
    {
      name: "The Henderson Family",
      location: "Spring Creek, NV",
      rating: 5,
      text: "Family of four heading to a destination wedding in Costa Rica. We realized the kids' passports were expired 2 weeks before departure. Fast Passport Center processed all four in 4 days. We made the wedding!",
      service: "Child Passport",
    },
    {
      name: "Tom B.",
      location: "Elko, NV",
      rating: 5,
      text: "Business owner in Elko needing a passport for a trade show in Germany. Even from Elko, the nearest agency is impossibly far. Fast Passport Center's courier service got it done in 3 days. This is essential infrastructure for Elko County.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "spring-creek-1",
      name: "Spring Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "740 Spring Valley Ct",
      city: "Spring Creek",
      state: "NV",
      zip: "89815",
      phone: "(775) 753-7877",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Spring Creek Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient Spring Creek location. Small facility — call ahead to confirm passport acceptance times. Appointments recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "spring-creek-2",
      name: "Elko Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "256 5th St",
      city: "Elko",
      state: "NV",
      zip: "89801",
      phone: "(775) 738-5026",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles east of Spring Creek",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest passport facility in northeastern Nevada. Full services with appointment. 15 miles from Spring Creek.",
      icon: "ri-mail-line",
    },
    {
      id: "spring-creek-3",
      name: "Elko County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "571 Idaho St",
      city: "Elko",
      state: "NV",
      zip: "89801",
      phone: "(775) 753-4600",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "15 miles east of Spring Creek",
      website: "https://www.elkocountynv.net/",
      websiteLabel: "Elko County Clerk",
      notes: "County government passport acceptance. Serves all of Elko County. Located in downtown Elko.",
      icon: "ri-government-line",
    },
    {
      id: "spring-creek-4",
      name: "Carlin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "110 S 8th St",
      city: "Carlin",
      state: "NV",
      zip: "89822",
      phone: "(775) 754-6364",
      hours: "Mon–Fri: 9AM–1PM, 2PM–4PM",
      distance: "30 miles west of Spring Creek",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small facility west on I-80. Limited hours — call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
    {
      id: "spring-creek-5",
      name: "Wells Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "157 7th St",
      city: "Wells",
      state: "NV",
      zip: "89835",
      phone: "(775) 752-3341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "50 miles east of Spring Creek",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Wells and eastern Elko County. 50 miles east on I-80. Call ahead for passport appointments.",
      icon: "ri-mail-line",
    },
    {
      id: "spring-creek-6",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89501",
      phone: "(775) 788-0900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "290 miles west via I-80",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest passport facility in Northern Nevada. 290 miles via I-80 — plan for a full day trip if visiting in person.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 480 miles to San Francisco", fpc: "No — ship from Elko UPS or home" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Spring Creek has no photo services — you'll need to go to Elko. Passport photos must be exactly 2×2 inches with a white or off-white background. Walmart and Walgreens in Elko are your best bets.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or wrong form type are the #1 reason for rejection at northeastern Nevada acceptance facilities like the Spring Creek Post Office or Elko County Clerk.",
    },
    {
      icon: "ri-road-map-line",
      title: "Massive Distance to Nearest Agency",
      description: "The nearest passport agency is 480 miles away in San Francisco — an 8+ hour drive across Nevada and the Sierra Nevada. Our courier service is the only practical option for Spring Creek travelers.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation, the San Francisco regional agency will turn you away — and you'll have driven 480 miles for nothing. From Spring Creek, that's a catastrophic waste of time.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This is especially challenging for Spring Creek families where one parent may work mining shifts in the Carlin Trend or Cortez operations.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no passport agency. Standard mail processing from northeastern Nevada takes longer than most places due to rural mail routing. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Elko." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Spring Creek address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Spring Creek, NV?",
      a: "Fast Passport Center offers emergency passport processing in Spring Creek in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Nevada has no local passport agency — the nearest is San Francisco, 480 miles away — so our courier service is the only practical fast option for Spring Creek and Elko County travelers.",
    },
    {
      q: "What is the nearest passport agency to Spring Creek?",
      a: "The nearest passport agency to Spring Creek is the San Francisco Passport Agency, approximately 480 miles west via I-80 — an 8+ hour drive across Nevada and over the Sierra Nevada. This is not remotely practical from Spring Creek. Salt Lake City is 230 miles east but has no passport agency either. Fast Passport Center's courier service is essential for this region.",
    },
    {
      q: "Does Spring Creek have a passport acceptance facility?",
      a: "Yes. The Spring Creek Post Office at 740 Spring Valley Ct offers passport acceptance services. However, being a small community facility, hours are limited and appointments are required. More comprehensive options include the Elko Main Post Office and Elko County Clerk, both about 15 miles away in downtown Elko.",
    },
    {
      q: "Do you serve mining industry families in Spring Creek?",
      a: "Absolutely. Spring Creek is a major residential community for Elko County's mining industry — serving the Carlin Trend, one of the world's richest gold mining districts. Nevada Gold Mines (Barrick/Newmont joint venture) and other operators employ thousands in the area. Fast Passport Center understands the urgent international travel needs of this industry and provides rapid passport processing for mining professionals and their families.",
    },
    {
      q: "What about the Ruby Mountains — do you serve the Lamoille area?",
      a: "Yes. Lamoille and the Ruby Mountain recreational area are just 10 miles from Spring Creek. Whether you're a full-time Lamoille resident or visiting the Ruby Mountains — Nevada's Swiss Alps — Fast Passport Center provides fast passport service to the entire region.",
    },
    {
      q: "Can I fly internationally from near Spring Creek?",
      a: "Elko Regional Airport is about 15 miles from Spring Creek. It offers connecting flights to Salt Lake City International Airport (SLC) — a major Delta hub with extensive international connections. Salt Lake City is also drivable at 230 miles via I-80. Fast Passport Center ensures your passport is ready well before departure.",
    },
    {
      q: "How much does expedited passport service cost in Spring Creek?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your departure date and processing speed.",
    },
  ],
  photoLocations: [
    { name: "Spring Creek Post Office", address: "740 Spring Valley Ct, Spring Creek, NV 89815", hours: "Mon–Fri: 9AM–4PM (limited photo service)" },
    { name: "Walmart — Elko", address: "2660 Mountain City Hwy, Elko, NV 89801", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Elko", address: "1795 Idaho St, Elko, NV 89801", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Elko", address: "2520 Mountain City Hwy, Elko, NV 89801", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–3PM" },
    { name: "CVS Pharmacy — Elko", address: "2525 Mountain City Hwy, Elko, NV 89801", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Elko Post Office", address: "256 5th St, Elko, NV 89801", hours: "Mon–Fri: 9AM–4PM (photo by appointment)" },
    { name: "FedEx Office — Salt Lake City (nearest)", address: "2180 S 1300 E, Salt Lake City, UT 84106", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "Walmart — Twin Falls, ID", address: "252 Cheney Dr W, Twin Falls, ID 83301", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Elko",
      address: "3920 E Idaho St, Elko, NV 89801",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Closest DMV to Spring Creek — about 15 miles via Lamoille Hwy. Full-service with REAL ID. Online appointment recommended.",
      featured: true,
    },
    {
      name: "Nevada DMV — Reno",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Primary Northern Nevada DMV. 290 miles from Spring Creek. REAL ID processing. Online appointment recommended.",
      featured: true,
    },
    {
      name: "Elko County Clerk — Elko",
      address: "571 Idaho St, Elko, NV 89801",
      hours: "Mon–Fri: 8AM–5PM",
      note: "County government services for Elko County residents. Located in downtown Elko. Passport acceptance available.",
      featured: false,
    },
    {
      name: "Nevada DMV — Winnemucca Express",
      address: "3200 Traders Way, Winnemucca, NV 89445",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Smaller DMV express office. About 170 miles west via I-80. Limited services — call ahead.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EKO",
      name: "Elko Regional Airport",
      distance: "15 miles from Spring Creek via Lamoille Hwy",
      address: "975 Terminal Way, Elko, NV 89801",
      note: "Closest airport to Spring Creek. Connecting flights to Salt Lake City (SLC) for international connections. 15-mile drive.",
      flightUrl: "https://www.flyelko.com/",
      flightLabel: "Elko Regional Airport",
      featured: true,
    },
    {
      code: "SLC",
      name: "Salt Lake City International Airport",
      distance: "230 miles from Spring Creek via I-80",
      address: "776 N Terminal Dr, Salt Lake City, UT 84122",
      note: "Major western hub and Delta Air Lines headquarters. Extensive international connections. 230-mile drive via I-80 — about 3.5 hours.",
      flightUrl: "https://www.slcairport.com/",
      flightLabel: "Salt Lake City Airport",
      featured: false,
    },
    {
      code: "TWF",
      name: "Magic Valley Regional Airport — Twin Falls",
      distance: "175 miles from Spring Creek via US-93",
      address: "492 Airport Loop, Twin Falls, ID 83301",
      note: "Regional airport with connections to Salt Lake City and Denver. Alternative for western Elko County travelers.",
      flightUrl: "https://www.tfid.org/",
      flightLabel: "Twin Falls Airport",
      featured: false,
    },
  ],
};